/**
 * Main base for the application.
 *
 * File: \src\App.ts
 * Created Date: Sunday, March 4th 2018, 16:55:14
 * Author: Sascha Herrmann
 * -----
 * Last Modified: July 16th 2024, 17:02:50
 * Modified By: Shahbaz Haider
 * -----
 * Copyright (c) 2017-2018 Liebherr-Hausgeräte GmbH
 */
import { defineComponent, nextTick, Ref, ref, onMounted } from 'vue';
import SupportedBrowsersDialog from '@/components/login/SupportedBrowsersDialog.vue';
import MaintenanceDialog from '@/components/login/MaintenanceDialog.vue';
import BrowserUtil from '@/components/general/util/BrowserUtil';
import MessageBus from '@/MessageBus';
import { useLoginStore } from './components/login/LoginStore';
import { useConfigStore } from './components/config/ConfigStore';
import { useHomeStore } from './views/HomeStore';

import { CubeLogger } from './Logger';

/**
 * The App class.
 * It shows the base of the application and the main menu.
 * The contents are rendered in a router-view.
 *
 */
export default defineComponent({
  components: {
    SupportedBrowsersDialog,
    MaintenanceDialog
  },
  setup(props) {
    /**
     * Checks if the browser is unsupported
     *
     */
    const unsupportedBrowser: Ref<boolean> = ref(false);

    /**
     * Checks if application is currently in maintenance
     *
     */
    const maintenance: Ref<boolean> = ref(false);

    /**
     * Object that we fill with the json from blobstorage when given
     *
     */
    const maintenanceObject = ref({});

    /** OnMounted - Life cycle hook */
    onMounted(() => {
      const logger = CubeLogger.$log.prefix('App::created');

      nextTick(() => {
        unsupportedBrowser.value = !BrowserUtil.isSupportedBrowser();

        if (!unsupportedBrowser.value) {
          MessageBus.onceUserAuthenticated(() => {
            logger.debug('User was authenticated, loading configs...');

            const configStore = useConfigStore();
            configStore.loadAllConfigs();

            const homeStore = useHomeStore();
            homeStore.readMessageRights();
            homeStore.readVersions();
          });

          MessageBus.onceUserConfigured(() => {
            logger.debug('User was configured, fetching SCH data, loading licenses and addons...');

            const loginStore = useLoginStore();
            loginStore.loadColleagues();
          });
        }
      });
    });

    return {
      unsupportedBrowser,
      maintenance,
      maintenanceObject
    };
  }
});
